/**
 * Notifications Documentation Content
 *
 * DOC-03: Complete Notifications documentation covering all 28 scheduled jobs
 * Provides guides for viewing, enabling/disabling, and testing notifications.
 */

export const notificationsMetadata = {
  id: 'notifications-overview',
  title: 'Notifications System Guide',
  description: 'Understand how notifications work: scheduled jobs, delivery channels, and troubleshooting.',
  category: 'notifications',
  roles: ['admin'],
  keywords: [
    'notifications', 'alerts', 'email', 'whatsapp', 'scheduled',
    'how notifications work', 'delivery', 'jobs', 'notification settings',
    'not receiving notifications', 'troubleshooting', 'send notification'
  ]
};

export const notificationsContent = {
  sections: [
    // Section 1: What are Notification Jobs?
    {
      heading: 'What are Notification Jobs?',
      content: `Notification jobs are automated tasks that run on a schedule. They check your data at specific times and send notifications when needed.

**Key points about jobs:**
- Jobs run automatically at their scheduled times - no human needs to trigger them
- Each job is designed for a specific purpose (like sending pulse summaries or reminders)
- Jobs can send emails, WhatsApp messages, or both
- You can enable or disable any job based on your needs`,
      tips: [
        'All job times are shown in IST (Indian Standard Time)',
        'Jobs run reliably whether or not anyone is using the system',
        'You can see when each job last ran and what happened'
      ]
    },

    // Section 2: How to View Notification Jobs
    {
      heading: 'How to View Notification Jobs',
      content: 'The Notifications page shows all scheduled jobs and their current status.',
      steps: [
        'Click "Notifications" in the sidebar navigation',
        'Click the "Jobs" tab to see all notification jobs',
        'Each job shows: name, schedule time (IST), channel, last run, status, and next run',
        'Enabled jobs have their toggle switch turned on (blue)',
        'Click any column header to sort the list'
      ],
      subsections: [
        {
          title: 'Understanding Job Status',
          items: [
            'Success (green) - Job ran and completed without errors',
            'Failed (red) - Job encountered an error - check logs for details',
            'Skipped (yellow) - Job was scheduled but conditions were not met to send',
            'Not run (gray) - Job has not run yet since being enabled'
          ]
        }
      ]
    },

    // Section 3: Notification Channels
    {
      heading: 'Notification Channels',
      content: 'Jobs can send notifications through different channels based on their purpose and urgency.',
      subsections: [
        {
          title: 'Email',
          description: 'Sent to your inbox. Best for: detailed summaries, reports, and information you want to read later. Emails can include formatted tables and longer content.'
        },
        {
          title: 'WhatsApp',
          description: 'Sent to your phone. Best for: urgent alerts that need immediate attention, reminders during the workday. WhatsApp messages are shorter and designed for quick reading.'
        },
        {
          title: 'Both',
          description: 'Sends both email and WhatsApp. Used for: critical alerts where you need both immediate attention (WhatsApp) and a detailed record (Email).'
        },
        {
          title: 'System',
          description: 'Internal jobs that run in the background without sending any notification. Used for: data sync, cleanup tasks, and maintenance.'
        }
      ]
    },

    // Section 4: Job Categories Explained
    {
      heading: 'All 28 Notification Jobs Explained',
      content: `Here is a complete list of all notification jobs organized by category. Each job has a specific purpose and runs at a scheduled time.`,
      subsections: [
        {
          title: 'Pulse Summaries (4 jobs)',
          description: 'These jobs send summary reports after each pulse window closes.',
          items: [
            'P1 Pulse Summary (12:00 IST) - Summary of morning pulse entries after P1 closes',
            'P2 Pulse Summary (16:00 IST) - Summary of afternoon pulse entries after P2 closes',
            'P3 Pulse Summary (20:00 IST) - Summary of evening pulse entries after P3 closes',
            'P4 Pulse Summary + Daily (22:00 IST) - Final pulse summary plus complete daily totals'
          ]
        },
        {
          title: 'Pre-Pulse Reminders (4 jobs)',
          description: 'These jobs remind team leaders that a pulse window is about to open.',
          items: [
            'P1 Pre-Pulse Reminder (7:55 IST) - "P1 starting in 5 minutes!"',
            'P2 Pre-Pulse Reminder (11:55 IST) - "P2 starting in 5 minutes!"',
            'P3 Pre-Pulse Reminder (15:55 IST) - "P3 starting in 5 minutes!"',
            'P4 Pre-Pulse Reminder (19:55 IST) - "P4 starting in 5 minutes!"'
          ]
        },
        {
          title: 'Pulse Start Notifications (4 jobs)',
          description: 'These jobs announce when a pulse window officially opens.',
          items: [
            'P1 Start (8:00 IST) - "P1 window is now open - submit your entries"',
            'P2 Start (12:00 IST) - "P2 window is now open - submit your entries"',
            'P3 Start (16:00 IST) - "P3 window is now open - submit your entries"',
            'P4 Start (20:00 IST) - "P4 window is now open - submit your entries"'
          ]
        },
        {
          title: 'Commitment Checks (4 jobs)',
          description: 'These jobs check for unfulfilled commitments and send reminders.',
          items: [
            'P1 Commitment Check (12:00 IST) - Checks for commitments not yet fulfilled after P1',
            'P2 Commitment Check (16:00 IST) - Checks for commitments not yet fulfilled after P2',
            'P3 Commitment Check (20:00 IST) - Checks for commitments not yet fulfilled after P3',
            'P4 Commitment Check (22:00 IST) - Final commitment check for the day'
          ]
        },
        {
          title: 'Overdue Reminders (3 jobs)',
          description: 'These jobs send repeated reminders for teams that have not submitted their pulse entries.',
          items: [
            'P1 Overdue Reminders (12:30-21:30 IST, every 30 min) - "You still need to submit P1!"',
            'P2 Overdue Reminders (16:30-21:30 IST, every 30 min) - "You still need to submit P2!"',
            'P3 Overdue Reminders (20:30-21:30 IST, every 30 min) - "You still need to submit P3!"'
          ]
        },
        {
          title: 'Verification Jobs (2 jobs)',
          description: 'These jobs remind managers and team leaders to verify pulse entries.',
          items: [
            'Verification Reminder (21:00 IST) - Daily email to managers listing unverified entries',
            'Recurring Verification Alerts (8:30-21:30 IST, every 30 min) - WhatsApp reminders for pending verifications'
          ]
        },
        {
          title: 'Admin Summaries (5 jobs)',
          description: 'Detailed summaries sent to admin users for oversight and review.',
          items: [
            'P1 Admin Summary (12:05 IST) - Detailed P1 summary for admins',
            'P2 Admin Summary (16:05 IST) - Detailed P2 summary for admins',
            'P3 Admin Summary (20:05 IST) - Detailed P3 summary for admins',
            'P4 Admin Summary (22:05 IST) - Detailed P4 summary for admins',
            'EOD Admin Summary (22:30 IST) - Complete end-of-day wrap-up report'
          ]
        },
        {
          title: 'Alert Jobs (1 job)',
          description: 'Special alert for performance issues requiring attention.',
          items: [
            'Red Flag Check (22:30 IST) - Reviews daily data for performance issues and sends alerts for any gyms that need follow-up'
          ]
        },
        {
          title: 'System Jobs (1 job)',
          description: 'Background maintenance tasks that do not send notifications.',
          items: [
            'Daily Gym Sync (12:00 IST) - Syncs gym data from master system (no notification sent, runs silently)'
          ]
        }
      ],
      tips: [
        'Job times are in IST - adjust your expectations if you are in a different timezone',
        'Overdue reminder jobs run multiple times until the entry is submitted',
        'Admin summaries provide more detail than regular summaries'
      ]
    },

    // Section 5: How to Enable/Disable a Job
    {
      heading: 'How to Enable or Disable a Job',
      content: 'You can turn any notification job on or off based on your needs.',
      steps: [
        'Go to the Notifications page',
        'Click the "Jobs" tab to see all jobs',
        'Find the job you want to change',
        'Click the toggle switch on the left side of the job row',
        'Blue switch = Enabled (job will run at scheduled time)',
        'Gray switch = Disabled (job will not run until re-enabled)'
      ],
      tips: [
        'Disabled jobs appear slightly faded in the list',
        'When you re-enable a job, it will run at its next scheduled time',
        'Disabling a job does not delete any history - you can see past runs',
        'Be careful disabling critical jobs like commitment checks'
      ]
    },

    // Section 6: How to Change Job Timing
    {
      heading: 'How to Change Job Timing',
      content: 'Job timing is configured by system administrators. Contact your admin if you need to adjust when a job runs.',
      subsections: [
        {
          title: 'What Can Be Changed',
          items: [
            'Job schedule (what time it runs)',
            'Recipients (who receives notifications)',
            'Job enabled/disabled status (can be changed by any admin)'
          ]
        },
        {
          title: 'What Cannot Be Changed',
          items: [
            'Job logic (what the job checks)',
            'Notification templates (standard formatting)',
            'Job names and categories'
          ]
        }
      ],
      tips: [
        'Timing changes take effect immediately - no restart needed',
        'Consider timezone implications when changing job times',
        'Document any timing changes so other admins are aware'
      ]
    },

    // Section 7: Testing Notifications
    {
      heading: 'Testing Notifications',
      content: 'Before relying on notifications, you can send test messages to verify your email and WhatsApp are configured correctly.',
      steps: [
        'Go to the Notifications page',
        'Click the "Test" tab',
        'To test email: Enter your email address and click "Send"',
        'To test WhatsApp: Enter your phone number (with country code, e.g., +91...) and click "Send"',
        'Check your inbox or phone for the test message',
        'If you do not receive it, check your spam folder or verify the contact details'
      ],
      subsections: [
        {
          title: 'Troubleshooting Test Failures',
          items: [
            'Email not received: Check spam folder, verify email address is correct',
            'WhatsApp not received: Ensure phone number includes country code (+91 for India)',
            'Error message: Contact admin to verify API keys are configured',
            'Delayed delivery: Wait 1-2 minutes, some messages have slight delays'
          ]
        }
      ],
      tips: [
        'Test both channels when setting up a new user',
        'Test after any configuration changes',
        'Keep a record of successful test results for troubleshooting'
      ]
    },

    // Section 8: Viewing Recent Notifications
    {
      heading: 'Viewing Recent Notifications',
      content: 'The Recent tab shows the last 50 notifications sent by the system.',
      steps: [
        'Go to the Notifications page',
        'Click the "Recent" tab',
        'View the list of recent notifications',
        'Use filters to narrow by channel (Email/WhatsApp) or status',
        'Click "Retry" on any failed notification to resend it'
      ],
      subsections: [
        {
          title: 'Understanding Notification Status',
          items: [
            'Sent (green) - Notification was successfully sent to the delivery service',
            'Delivered (green) - Notification confirmed delivered to recipient',
            'Failed (red) - Notification failed to send - check error message',
            'Skipped (yellow) - Notification was not sent because conditions were not met'
          ]
        },
        {
          title: 'Retrying Failed Notifications',
          description: 'If a notification failed due to a temporary issue (like network problems), you can retry it. Click the "Retry" button next to any failed notification. The system will attempt to send it again immediately.'
        }
      ],
      tips: [
        'The list shows the 50 most recent notifications',
        'Failed notifications should be investigated and retried',
        'Skipped notifications are normal - they mean conditions were not met'
      ]
    }
  ]
};

export default notificationsContent;
